import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, 
  FaCheckCircle, 
  FaBuilding, 
  FaUsers, 
  FaChartLine,
  FaTrophy,
  FaRocket,
  FaShieldAlt,
  FaClock,
  FaFilter,
  FaTimes,
  FaGraduationCap,
  FaGlobeAfrica,
  FaMobileAlt,
  FaCloud,
  FaUniversity,
  FaHandshake,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaQuoteLeft,
  FaBookOpen,
  FaLightbulb,
  FaPlayCircle,
  FaEye,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { useState } from 'react';

const caseStudies = [
  // ===== GROWTH STORIES =====
  {
    id: 1,
    title: 'From One Generation of PowerFinancials to the Next',
    client: 'Dream Credit',
    industry: 'Microfinance',
    category: 'Growth Stories',
    description: 'A long-standing PowerFinancials relationship now entering its next chapter with a next-generation platform upgrade.',
    fullStory: `Dream Credit has been one of Micropoint's most loyal PowerFinancials clients. The relationship is a strong example of continuity: as Dream Credit's operations and requirements have evolved, so has PowerFinancials.

Today, Micropoint is upgrading Dream Credit's existing PowerFinancials environment to the next generation of the platform, bringing newer capabilities, improved workflows and a modern technology foundation while preserving the value of the organization's long-standing experience with the system.

This is more than a software upgrade. It is a long-standing relationship entering its next chapter. It demonstrates Micropoint's ability to support organizations over time, continuously improve the technology and evolve the platform as the client's needs change.`,
    results: [
      'Next-generation platform upgrade',
      'Preserved institutional knowledge',
      'Modern technology foundation',
      'Continued long-term partnership'
    ],
    image: '/assets/case-studies/dream-credit.jpg',
    featured: false,
    gradient: 'from-blue-500 to-indigo-600',
    icon: FaRocket,
    pullQuote: '"A long-standing client. A growing business. A technology platform that continues to evolve."',
    tags: ['Growth', 'PowerFinancials', 'Upgrade']
  },
  {
    id: 2,
    title: 'From a Small Office in Kiambu to a Growing Regulated SACCO',
    client: 'DIMKES SACCO',
    industry: 'SACCO',
    category: 'Growth Stories',
    description: 'A small Kiambu office, PowerFinancials, rapid growth and a journey that eventually led to Microsoft Dynamics 365 Business Central.',
    fullStory: `In 2010, Micropoint encountered DIMKES SACCO in a very different stage of its journey. The organization was operating from a small office on the second floor of Bishop Magua Building in Kiambu town, where Lucy and Sarah were running the organization.

Micropoint installed PowerFinancials to support the SACCO's financial and operational management. Over the following years, DIMKES grew, became licensed by SASRA and expanded to 11 branches across the Mt. Kenya region.

As the organization continued to grow, its technology requirements evolved. DIMKES eventually moved from PowerFinancials to Microsoft Dynamics 365 Business Central.

For Micropoint, this remains a meaningful growth story. It is not about a client remaining on one system forever. It is about being there at an early stage, supporting growth and recognizing when an organization has reached the next stage of its technology journey.`,
    results: [
      'Grew from 1 to 11 branches',
      'Achieved SASRA regulation',
      'Scaled to Microsoft Dynamics 365',
      'Expanded across Mt. Kenya region'
    ],
    image: '/assets/case-studies/dimkes.jpg',
    featured: false,
    gradient: 'from-emerald-500 to-teal-600',
    icon: FaRocket,
    pullQuote: '"We first met DIMKES when it was operating from a small office in Kiambu. Watching the organization grow from those early days into a substantial regulated SACCO remains one of the journeys we are proud to have witnessed."',
    tags: ['Growth', 'SACCO', 'PowerFinancials', 'Dynamics 365']
  },
  {
    id: 3,
    title: 'From One Employee to a Growing Credit Business',
    client: 'MyCredit',
    industry: 'Microfinance',
    category: 'Growth Stories',
    description: 'A one-employee startup, PowerFinancials and a growth journey that eventually took MyCredit into a much larger operating environment.',
    fullStory: `MyCredit began its journey in 2016 with just one employee. At that early stage, the business adopted PowerFinancials to support its financial operations.

For approximately three years, PowerFinancials formed part of MyCredit's technology environment as the business grew. As the organization expanded and its requirements evolved, it eventually moved beyond PowerFinancials.

Today, MyCredit is a substantially larger credit business. Its growth makes the early relationship with Micropoint a compelling example of technology supporting an organization during its formative years.

The most powerful point is not that MyCredit eventually moved on from PowerFinancials. It is that Micropoint was there when the organization had only one employee.`,
    results: [
      'From 1 employee to substantial credit business',
      'Scalable technology foundation',
      'Supported early-stage growth',
      'Long-term partnership'
    ],
    image: '/assets/case-studies/mycredit.jpg',
    featured: false,
    gradient: 'from-purple-500 to-indigo-600',
    icon: FaRocket,
    pullQuote: '"We were there when MyCredit was just getting started—with one employee and a business still finding its feet. Being part of that early technology journey is something we are proud of."',
    tags: ['Growth', 'Microfinance', 'Startup']
  },

  // ===== ENTERPRISE TRANSFORMATION =====
  {
    id: 4,
    title: 'From Core Finance to a Connected Digital Business',
    client: 'Springboard Capital',
    industry: 'Microfinance',
    category: 'Enterprise Transformation',
    description: 'A connected Dynamics 365 environment bringing credit, finance, procurement, HR, payroll, ESS, CRM and mobile lending together.',
    fullStory: `Springboard Capital represents one of Micropoint's strongest Microsoft Dynamics 365 implementations. The project brought together a broad range of business functions rather than treating finance, credit, people and customer processes as isolated systems.

Micropoint successfully implemented a connected Dynamics 365 environment covering credit, finance, procurement, HR and payroll, employee self-service, a mobile lending application, CRM and related business workflows.

The result is a technology environment in which core financial operations, credit processes, procurement, people management, customer relationship management and digital lending can work together.

Springboard demonstrates Micropoint's ability to implement Microsoft technology for a financial institution and extend it with surrounding applications and business processes. It is a strong example of Micropoint's proposition that enterprise technology should be configurable and extendable rather than forcing an organization to change the way it works.`,
    results: [
      'Integrated Dynamics 365 implementation',
      'Connected credit, finance, HR & procurement',
      'Mobile lending application',
      'Enterprise-wide business workflows'
    ],
    image: '/assets/case-studies/springboard.jpg',
    featured: true,
    gradient: 'from-blue-600 to-indigo-700',
    icon: FaCloud,
    pullQuote: '"One connected technology environment supporting the financial institution\'s operations."',
    tags: ['Enterprise', 'Dynamics 365', 'Digital Transformation']
  },

  // ===== DIGITAL & INTEGRATION =====
  {
    id: 5,
    title: 'When Security Couldn\'t Be Compromised: The Mkopo Radi Story',
    client: 'Bands SACCO',
    industry: 'SACCO',
    category: 'Digital & Integration',
    description: 'After years of searching for a provider capable of working within demanding security requirements, Bands SACCO found a technology partner in Micropoint.',
    fullStory: `Bands SACCO, associated with the Britam ecosystem, had been searching for a technology provider capable of delivering a mobile solution within a demanding security environment. The search had continued for approximately three years before Micropoint engaged with the SACCO in 2018.

Bands SACCO needed to extend its member services into mobile lending while operating within a highly secure technology environment. The combination of mobile lending requirements and strict security and integration requirements made the project a demanding one.

Micropoint worked with Bands SACCO to deliver a mobile lending solution supporting the SACCO's mobile loan product, Mkopo Radi.

Since the implementation, Bands SACCO has been able to serve its members through Mkopo Radi, giving the SACCO a practical digital channel for mobile lending and strengthening its ability to deliver services to members.`,
    results: [
      'Secure mobile lending platform',
      'Mkopo Radi product launched',
      '3-year search ended',
      'Member service enhancement'
    ],
    image: '/assets/case-studies/bands-sacco.jpg',
    featured: false,
    gradient: 'from-amber-500 to-orange-600',
    icon: FaMobileAlt,
    pullQuote: '"Sometimes the hardest integrations are the ones that matter most."',
    tags: ['Security', 'Mobile Lending', 'SACCO', 'Integration']
  },
  {
    id: 6,
    title: 'Connecting Financial Institutions to the Banking Ecosystem',
    client: 'Banking & Financial Integrations',
    industry: 'Fintech',
    category: 'Digital & Integration',
    description: 'From bank connectivity and payment collections to cheque truncation and CRB integration.',
    fullStory: `Micropoint has delivered and worked on integrations connecting financial institutions and client systems with banks, payment channels and credit-reference services.

Selected integration experience includes:
• NCBA Bank — account-level transaction notification / push integration
• Co-operative Bank — remote cheque scanning / cheque truncation workflow
• Family Bank — cheque truncation solution for a microfinance/SACCO client
• Equity Bank — integration project associated with Kwale County
• KCB — integration currently being finalized
• Metropol CRB — credit-reference integration supporting identity verification, delinquency checks, credit scores and credit reports

Our integration work spans bank APIs, transaction notifications, M-PESA and mobile money, payment collections, automated reconciliation, bank statements, payment gateways, cheque processing, CRB connectivity and financial transaction messaging.`,
    results: [
      'Integrated with 6 major banks',
      'Real-time transaction processing',
      'Cheque truncation workflow',
      'CRB integration capabilities'
    ],
    image: '/assets/case-studies/banking-integrations.jpg',
    featured: false,
    gradient: 'from-cyan-500 to-blue-600',
    icon: FaHandshake,
    pullQuote: '"Connecting financial institutions to the banking ecosystem through reliable integrations."',
    tags: ['Integration', 'Banking', 'API', 'CRB']
  },

  // ===== FINANCIAL INCLUSION & REGIONAL =====
  {
    id: 7,
    title: 'Technology Supporting Financial Inclusion',
    client: 'Destiny Women Capital',
    industry: 'Microfinance',
    category: 'Financial Inclusion',
    description: 'How financial technology can support a growing women-focused credit and financial ecosystem.',
    fullStory: `Destiny Women Capital is a strong financial-inclusion story for Micropoint. The organization operates within a women-focused financial ecosystem, making the story an opportunity to show how technology supports access to credit and structured financial operations.

Micropoint's story should focus on the role of technology in supporting credit and financial management, the operational challenges that needed to be addressed and how the solution helped the organization serve its market.

The wider story is about technology enabling financial services to reach and support women and community-based financial groups at scale.`,
    results: [
      'Women-focused financial inclusion',
      'Increased access to capital',
      'Community economic development',
      'Scalable financial services'
    ],
    image: '/assets/case-studies/destiny-women.jpg',
    featured: false,
    gradient: 'from-pink-500 to-rose-600',
    icon: FaUsers,
    pullQuote: '"Technology should make financial services easier to manage, easier to scale and easier to deliver."',
    tags: ['Financial Inclusion', 'Women', 'Microfinance']
  },
  {
    id: 8,
    title: 'Tailoring Financial Technology for a Mogadishu Institution',
    client: 'KOBCIYE LLC',
    industry: 'Fintech',
    category: 'Financial Inclusion',
    description: 'PowerFinancials tailored for a Mogadishu-based microfinance institution.',
    fullStory: `KOBCIYE LLC is a Mogadishu-based microfinance institution using Micropoint's financial technology. The relationship demonstrates Micropoint's ability to tailor financial systems to operating environments outside Kenya.

The KOBCIYE implementation is a strong customization story: financial technology is adapted around the institution's operating requirements, workflows and controls rather than forcing the institution into a rigid model.

It also demonstrates Micropoint's regional reach and its ability to support financial institutions operating in different markets.`,
    results: [
      'Tailored for Mogadishu market',
      'Customized workflows and controls',
      'Regional expansion',
      'Cross-border capability'
    ],
    image: '/assets/case-studies/kobil.jpg',
    featured: false,
    gradient: 'from-yellow-500 to-amber-600',
    icon: FaGlobeAfrica,
    pullQuote: '"Financial technology adapted to local operating environments across the region."',
    tags: ['Regional', 'Customization', 'PowerFinancials', 'Somalia']
  },
  {
    id: 9,
    title: 'Scaling Payroll: Supporting More Than 1,500 Agents in Kampala',
    client: 'Platinum Credit',
    industry: 'Microfinance',
    category: 'Financial Inclusion',
    description: 'Payroll technology supporting a distributed workforce of more than 1,500 agents in Kampala.',
    fullStory: `Micropoint's HR and Payroll solution is used by Platinum Credit to support payroll for more than 1,500 agents in Kampala, Uganda.

The story demonstrates that Micropoint's payroll capability is not limited to small organizations. It can support large, distributed workforces where accurate processing, employee records and reliable payroll operations are critical.

It is also a strong regional proof point: Micropoint technology is being used to support operations beyond Kenya.`,
    results: [
      '1,500+ agents supported',
      'Distributed workforce payroll',
      'Regional operations in Uganda',
      'Scalable HR solution'
    ],
    image: '/assets/case-studies/platinum-credit.jpg',
    featured: false,
    gradient: 'from-green-500 to-emerald-600',
    icon: FaGlobeAfrica,
    pullQuote: '"Payroll technology supporting a distributed workforce at scale across borders."',
    tags: ['Payroll', 'Regional', 'HR', 'Uganda']
  },

  // ===== BESPOKE ENTERPRISE =====
  {
    id: 10,
    title: 'Beyond Financial Software: Building Bespoke Enterprise Systems',
    client: 'Kenya Roads Board',
    industry: 'Government',
    category: 'Bespoke Enterprise',
    description: 'Building bespoke enterprise systems around real operational requirements.',
    fullStory: `Micropoint's work extends beyond packaged financial systems into bespoke enterprise software. The company's project experience includes solutions associated with Kenya Roads Board, including monitoring and contracts-management systems.

This demonstrates an important part of Micropoint's capability: when a standard product does not fully address a business process, the team can design and build software around the organization's requirements.`,
    results: [
      'Custom monitoring system',
      'Contracts-management solution',
      'Tailored enterprise software',
      'Real operational requirements'
    ],
    image: '/assets/case-studies/krb.jpg',
    featured: false,
    gradient: 'from-slate-600 to-blue-700',
    icon: FaBuilding,
    pullQuote: '"Custom enterprise solutions built around real operational requirements."',
    tags: ['Bespoke', 'Enterprise', 'Government']
  },

  // ===== EDUCATION =====
  {
    id: 11,
    title: 'A Long-Term Partnership in School Management',
    client: 'Zasa Academy',
    industry: 'Education',
    category: 'Education',
    description: 'A long-standing school management relationship built around dependable technology and continued support.',
    fullStory: `Patrick Langat, Director/owner of Zasa Academy, represents a long-standing relationship with Micropoint's school management software.

The story is about continuity: a school using technology over many years and a software provider that continues to support the institution as its needs evolve.

Zasa Academy gives Micropoint an opportunity to demonstrate that its technology experience extends beyond financial institutions into education and school administration.`,
    results: [
      'Long-term school management partnership',
      'Dependable technology',
      'Continued support',
      'Education sector experience'
    ],
    image: '/assets/case-studies/zasa-academy.jpg',
    featured: false,
    gradient: 'from-sky-500 to-blue-600',
    icon: FaGraduationCap,
    pullQuote: '"Technology that supports educational institutions over the long term."',
    tags: ['Education', 'School Management', 'Long-term']
  }
];

const categories = [
  { id: 'All', label: 'All Case Studies' },
  { id: 'Growth Stories', label: '🚀 Growth Stories' },
  { id: 'Enterprise Transformation', label: '🏢 Enterprise Transformation' },
  { id: 'Digital & Integration', label: '📱 Digital & Integration' },
  { id: 'Financial Inclusion', label: '🌍 Financial Inclusion & Regional' },
  { id: 'Bespoke Enterprise', label: '⚙️ Bespoke Enterprise' },
  { id: 'Education', label: '🎓 Education' },
];

const stats = [
  { number: '150+', label: 'Clients Served', icon: FaUsers, color: 'from-blue-500 to-indigo-600' },
  { number: '70%', label: 'Average Efficiency Gain', icon: FaChartLine, color: 'from-emerald-500 to-teal-600' },
  { number: '99.9%', label: 'Uptime', icon: FaShieldAlt, color: 'from-amber-500 to-orange-600' },
  { number: '24/7', label: 'Support Available', icon: FaClock, color: 'from-purple-500 to-pink-600' },
];

export default function CaseStudies() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedStudy, setSelectedStudy] = useState(null);

  const filteredStudies = activeFilter === 'All' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeFilter);

  const featuredStudy = filteredStudies.find(study => study.featured);
  const regularStudies = filteredStudies.filter(study => !study.featured);

  // Modal navigation
  const currentIndex = selectedStudy ? caseStudies.findIndex(s => s.id === selectedStudy.id) : -1;
  const nextStudy = currentIndex < caseStudies.length - 1 ? caseStudies[currentIndex + 1] : null;
  const prevStudy = currentIndex > 0 ? caseStudies[currentIndex - 1] : null;

  const openModal = (study) => {
    setSelectedStudy(study);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedStudy(null);
    document.body.style.overflow = 'auto';
  };

  const navigateStudy = (direction) => {
    if (direction === 'next' && nextStudy) {
      setSelectedStudy(nextStudy);
    } else if (direction === 'prev' && prevStudy) {
      setSelectedStudy(prevStudy);
    }
  };

  return (
    <section className="py-16 md:py-20 bg-slate-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* ─── Header ─── */}
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-blue-200">
              Real Businesses. Real Growth. Real Technology.
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight">
              Case <span className="font-semibold text-blue-600">Studies</span>
            </h1>
            <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto text-sm">
              Real success stories from organizations that have transformed their operations with Micropoint Systems
            </p>
          </motion.div>
        </div>

        {/* ─── Stats ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="bg-white rounded-xl p-5 text-center border border-slate-100 hover:border-blue-200 transition-all hover:shadow-lg group"
              >
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mx-auto mb-2.5 group-hover:scale-110 transition-all shadow-sm`}>
                  <Icon className="text-lg text-white" />
                </div>
                <div className="text-xl font-bold text-blue-600">{stat.number}</div>
                <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>

        {/* ─── Category Filter ─── */}
        <div className="flex flex-wrap justify-center gap-1.5 mb-8">
          {categories.map((category) => {
            const isActive = activeFilter === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                    : 'bg-white text-slate-600 hover:text-blue-600 hover:bg-slate-50 border border-slate-200 hover:border-blue-300'
                }`}
              >
                {category.label}
              </button>
            );
          })}
        </div>

        {/* ─── Results Count ─── */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-sm text-slate-500">
            <span className="font-medium text-slate-700">{filteredStudies.length}</span> case studies
          </p>
          {activeFilter !== 'All' && (
            <button
              onClick={() => setActiveFilter('All')}
              className="text-sm text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-1 font-medium"
            >
              <FaTimes className="text-xs" /> Clear filter
            </button>
          )}
        </div>

        {/* ─── Case Studies Grid ─── */}
        {filteredStudies.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Case Studies Found</h3>
            <p className="text-slate-500">Try selecting a different category.</p>
          </div>
        ) : (
          <div className="space-y-8">
            
            {/* ─── Featured Case Study ─── */}
            {featuredStudy && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="group"
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
                  <div className="grid lg:grid-cols-2 gap-0">
                    <div className={`h-56 lg:h-auto bg-gradient-to-br ${featuredStudy.gradient} flex items-center justify-center relative overflow-hidden p-8`}>
                      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="white" fill-opacity="0.1"%3E%3Cpath d="M30 0L0 30l30 30 30-30z"/%3E%3C/g%3E%3C/svg%3E")' }} />
                      <div className="relative z-10 text-center">
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-white text-xs font-medium border border-white/20 mb-4">
                          <FaTrophy className="text-yellow-400" />
                          Featured Success Story
                        </div>
                        <span className="text-7xl drop-shadow-2xl block mb-2">🏆</span>
                        <p className="text-white/80 text-sm font-medium max-w-xs mx-auto">
                          {featuredStudy.pullQuote}
                        </p>
                      </div>
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="inline-block bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full flex items-center gap-1.5 font-medium border border-blue-100">
                          <FaBuilding className="text-xs" /> {featuredStudy.industry}
                        </span>
                        <span className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                          ⭐ Featured
                        </span>
                        <span className="inline-block bg-slate-100 text-slate-600 text-xs px-3 py-1 rounded-full font-medium">
                          {featuredStudy.category}
                        </span>
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                        {featuredStudy.title}
                      </h2>
                      <p className="text-sm text-blue-600 font-semibold mb-2">{featuredStudy.client}</p>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4">
                        {featuredStudy.description}
                      </p>
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                          Key Results
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {featuredStudy.results.map((result, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                              <FaCheckCircle className="text-blue-500 flex-shrink-0 text-xs" />
                              <span>{result}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => openModal(featuredStudy)}
                        className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-all group-hover:gap-3 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 w-fit"
                      >
                        Read Full Case Study <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ─── Regular Case Studies ─── */}
            {regularStudies.length > 0 && (
              <div className="grid md:grid-cols-2 gap-6">
                {regularStudies.map((study, index) => {
                  const Icon = study.icon;
                  return (
                    <motion.div
                      key={study.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="group"
                    >
                      <div className="bg-white rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300 h-full flex flex-col overflow-hidden">
                        <div className={`h-48 bg-gradient-to-br ${study.gradient} flex items-center justify-center relative overflow-hidden cursor-pointer`} onClick={() => openModal(study)}>
                          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="white" fill-opacity="0.1"%3E%3Cpath d="M30 0L0 30l30 30 30-30z"/%3E%3C/g%3E%3C/svg%3E")' }} />
                          <div className="relative z-10 flex flex-col items-center">
                            <Icon className="text-5xl text-white drop-shadow-lg" />
                            <span className="text-white/80 text-xs mt-2 font-medium bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full">
                              {study.category}
                            </span>
                          </div>
                          <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 transition-all text-white bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                              <FaEye /> Read Story
                            </span>
                          </div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="inline-block bg-blue-50 text-blue-700 text-xs px-2.5 py-0.5 rounded-full flex items-center gap-1 font-medium border border-blue-100">
                              <FaBuilding className="text-[10px]" /> {study.industry}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                            {study.title}
                          </h3>
                          <p className="text-sm text-blue-600 font-medium mb-2">{study.client}</p>
                          <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
                            {study.description}
                          </p>
                          
                          {/* Pull Quote */}
                          {study.pullQuote && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <FaQuoteLeft className="text-blue-400 text-xs mb-1" />
                              <p className="text-sm text-slate-700 italic leading-relaxed">
                                {study.pullQuote}
                              </p>
                            </div>
                          )}
                          
                          <div className="mb-4">
                            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                              Key Results
                            </h4>
                            <div className="space-y-1">
                              {study.results.slice(0, 3).map((result, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                  <FaCheckCircle className="text-blue-500 flex-shrink-0 text-xs" />
                                  <span className="line-clamp-1">{result}</span>
                                </div>
                              ))}
                              {study.results.length > 3 && (
                                <div className="text-xs text-slate-400 font-medium">
                                  +{study.results.length - 3} more results
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => openModal(study)}
                            className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-all group-hover:gap-3 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 w-fit mt-auto"
                          >
                            Read Full Case Study <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── CTA Section ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative overflow-hidden rounded-2xl p-10 md:p-12 text-center text-white mt-12"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.08)_0%,_transparent_70%)]" />
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 border border-white/10">
              <FaTrophy className="text-2xl text-blue-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
              Ready to Write <span className="text-blue-400">Your Success Story</span>?
            </h2>
            <p className="text-white/60 text-sm mb-6 max-w-lg mx-auto">
              Join 150+ organizations that have transformed their operations with Micropoint Systems. Whether you're starting, digitizing, integrating or evolving, we can help.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link 
                to="/contact" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold text-sm transition-all hover:shadow-lg hover:shadow-blue-600/30 hover:-translate-y-0.5"
              >
                Talk to Our Team
              </Link>
              <Link 
                to="/demo" 
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-full font-semibold text-sm border border-white/20 hover:border-white/30 transition-all hover:-translate-y-0.5"
              >
                Request a Demo
              </Link>
            </div>
          </div>
        </motion.div>

      </div>

      {/* ─── Modal ─── */}
      <AnimatePresence>
        {selectedStudy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.3, type: 'spring', damping: 25 }}
              className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="sticky top-0 float-right z-10 m-4 bg-white/90 backdrop-blur-sm text-slate-600 hover:text-slate-900 w-10 h-10 rounded-full flex items-center justify-center shadow-lg border border-slate-200 hover:border-slate-300 transition-all"
              >
                <FaTimes className="text-sm" />
              </button>

              {/* Modal content */}
              <div className="p-6 md:p-8 pt-0">
                {/* Header section with gradient */}
                <div className={`-mx-6 md:-mx-8 px-6 md:px-8 pt-8 pb-6 bg-gradient-to-br ${selectedStudy.gradient} text-white rounded-t-2xl`}>
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium border border-white/20">
                      {selectedStudy.category}
                    </span>
                    <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium border border-white/20">
                      {selectedStudy.industry}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-1">{selectedStudy.title}</h2>
                  <p className="text-white/80 text-sm font-medium">{selectedStudy.client}</p>
                </div>

                <div className="mt-6">
                  {/* Tags */}
                  {selectedStudy.tags && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {selectedStudy.tags.map((tag, i) => (
                        <span key={i} className="bg-blue-50 text-blue-700 text-xs px-2.5 py-0.5 rounded-full font-medium border border-blue-100">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Pull Quote */}
                  {selectedStudy.pullQuote && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <FaQuoteLeft className="text-blue-400 text-sm mb-1.5" />
                      <p className="text-base text-slate-700 italic leading-relaxed">
                        {selectedStudy.pullQuote}
                      </p>
                    </div>
                  )}

                  {/* Full Story */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                      <FaBookOpen className="text-blue-500" />
                      The Story
                    </h3>
                    <div className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                      {selectedStudy.fullStory}
                    </div>
                  </div>

                  {/* Key Results */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                      <FaLightbulb className="text-blue-500" />
                      Key Results
                    </h3>
                    <div className="grid md:grid-cols-2 gap-2">
                      {selectedStudy.results.map((result, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                          <FaCheckCircle className="text-blue-500 flex-shrink-0 text-xs" />
                          <span>{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <button
                      onClick={() => navigateStudy('prev')}
                      disabled={!prevStudy}
                      className={`flex items-center gap-2 text-sm font-medium transition-all ${
                        prevStudy 
                          ? 'text-blue-600 hover:text-blue-700 hover:gap-3' 
                          : 'text-slate-300 cursor-not-allowed'
                      }`}
                    >
                      <FaChevronLeft className="text-xs" />
                      Previous
                    </button>
                    <span className="text-xs text-slate-400 font-medium">
                      {currentIndex + 1} of {caseStudies.length}
                    </span>
                    <button
                      onClick={() => navigateStudy('next')}
                      disabled={!nextStudy}
                      className={`flex items-center gap-2 text-sm font-medium transition-all ${
                        nextStudy 
                          ? 'text-blue-600 hover:text-blue-700 hover:gap-3' 
                          : 'text-slate-300 cursor-not-allowed'
                      }`}
                    >
                      Next
                      <FaChevronRight className="text-xs" />
                    </button>
                  </div>

                  {/* CTA within modal */}
                  <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap gap-3">
                    <Link 
                      to="/contact" 
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all hover:shadow-md"
                    >
                      Talk to Our Team
                    </Link>
                    <Link 
                      to="/demo" 
                      className="border border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all"
                    >
                      Request a Demo
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}